/**
 * Admin Authentication Context
 * Manages admin user authentication state separate from customer auth
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { adminAuth, AdminUser, AdminSession } from '@/services/AdminAuthService';
import { logger } from '@/utils/logger';

interface AdminAuthContextType {
  user: AdminUser | null;
  session: AdminSession | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isSupport: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [session, setSession] = useState<AdminSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // Check for existing session in localStorage
      const storedSession = localStorage.getItem('admin_session');
      if (storedSession) {
        const parsedSession: AdminSession = JSON.parse(storedSession);

        // Validate the session with the backend
        const validatedUser = await adminAuth.validateSession(parsedSession.session_token);

        if (validatedUser) {
          setUser(validatedUser);
          setSession(parsedSession);

          // Update the service's current session
          adminAuth['currentSession'] = parsedSession;

          logger.info('Admin session restored', { user_id: validatedUser.user_id });
        } else {
          // Session is invalid, clear it
          localStorage.removeItem('admin_session');
        }
      }
    } catch (error) {
      logger.error('Failed to initialize admin auth', { error });
      localStorage.removeItem('admin_session');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);

      const result = await adminAuth.login({
        username,
        password,
        ip_address: await getClientIP(),
        user_agent: navigator.userAgent
      });

      if (result.success && result.user && result.session) {
        setUser(result.user);
        setSession(result.session);

        // Store session in localStorage
        localStorage.setItem('admin_session', JSON.stringify(result.session));

        logger.info('Admin login successful', {
          user_id: result.user.user_id,
          role: result.user.role
        });

        return true;
      } else {
        logger.warn('Admin login failed', { username, error: result.error });
        return false;
      }
    } catch (error) {
      logger.error('Admin login error', { error });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);

      if (session) {
        await adminAuth.logout(session.session_token);
      }

      // Clear state
      setUser(null);
      setSession(null);
      localStorage.removeItem('admin_session');

      logger.info('Admin logout successful');
    } catch (error) {
      logger.error('Admin logout error', { error });
    } finally {
      setLoading(false);
    }
  };

  const refreshSession = async (): Promise<void> => {
    if (!session) return;

    try {
      const validatedUser = await adminAuth.validateSession(session.session_token);

      if (validatedUser) {
        setUser(validatedUser);
      } else {
        // Session is no longer valid
        await logout();
      }
    } catch (error) {
      logger.error('Failed to refresh admin session', { error });
      await logout();
    }
  };

  const getClientIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  };

  const contextValue: AdminAuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!user && !!session,
    isAdmin: user?.role === 'admin',
    isModerator: user?.role === 'moderator',
    isSupport: user?.role === 'support',
    login,
    logout,
    refreshSession
  };

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export default AdminAuthContext;